import React from 'react';
import {
  LayoutTemplate, Package, UserCog, Mail, Activity, MessageCircle, ShieldCheck, Globe, Wallet,
  PanelLeftClose, PanelLeftOpen, ShieldAlert, LogOut,
} from 'lucide-react';
import { TabsList, TabsTrigger } from '../ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { DIRECTORY_ENABLED } from '../../config/features';

export const ADMIN_NAV_SECTIONS = [
  {
    kicker: 'Operations',
    items: [
      { id: 'deliveries', label: 'Deliveries', icon: Package, testid: 'main-tab-deliveries', description: 'Jobs, revenue, fees & refunds' },
      { id: 'users', label: 'User Management', icon: UserCog, testid: 'main-tab-users', description: 'Drivers, customers & approvals' },
      { id: 'earnings', label: 'Earnings & Revenue', icon: Wallet, testid: 'main-tab-earnings', description: 'Driver earnings & platform revenue' },
    ],
  },
  {
    kicker: 'Comms & Compliance',
    items: [
      { id: 'support', label: 'Support Inbox', icon: Mail, testid: 'main-tab-support', description: 'Customer & driver enquiries', badge: 'support' },
      { id: 'conversations', label: 'Conversations', icon: MessageCircle, testid: 'main-tab-conversations', description: 'In-app message threads' },
      { id: 'insurance', label: 'Insurance', icon: ShieldCheck, testid: 'main-tab-insurance', description: 'Driver insurance declarations' },
      { id: 'directory', label: 'Directory Approvals', icon: Globe, testid: 'main-tab-directory', description: 'Public listing requests', requiresDirectory: true },
    ],
  },
  {
    kicker: 'Website',
    items: [
      { id: 'cms', label: 'Homepage Content', icon: LayoutTemplate, testid: 'main-tab-cms', description: 'Edit homepage text, images & banners' },
    ],
  },
  {
    kicker: 'System',
    items: [
      { id: 'activity', label: 'Activity Log', icon: Activity, testid: 'main-tab-activity', description: 'Everything that happened' },
    ],
  },
];

export const ADMIN_NAV_LOOKUP = ADMIN_NAV_SECTIONS.flatMap((s) => s.items)
  .reduce((acc, item) => ({ ...acc, [item.id]: item }), {});

const NavItem = ({ item, collapsed, supportUnread, testidPrefix, onNavigate }) => {
  const Icon = item.icon;
  const unread = item.badge === 'support' ? supportUnread : 0;
  const trigger = (
    <TabsTrigger
      value={item.id}
      onClick={onNavigate}
      className={`group relative w-full h-11 rounded-lg border border-transparent text-sm font-medium text-slate-600
        data-[state=active]:bg-emerald-50 data-[state=active]:text-[#1B5E20] data-[state=active]:font-semibold
        data-[state=active]:border-emerald-200/80 data-[state=active]:shadow-none hover:bg-slate-100/80 hover:text-slate-900
        focus-visible:ring-2 focus-visible:ring-[#2E7D32] focus-visible:outline-none
        transition-colors duration-200 ${collapsed ? 'justify-center px-0' : 'justify-start px-3.5 gap-3'}`}
      data-testid={`${testidPrefix}${item.testid}`}
    >
      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-6 rounded-r-full bg-[#2E7D32] opacity-0 group-data-[state=active]:opacity-100 transition-opacity duration-200" />
      <Icon className="w-5 h-5 flex-shrink-0 text-slate-500 group-hover:text-slate-700 group-data-[state=active]:text-[#2E7D32] transition-transform group-hover:scale-105" strokeWidth={1.75} />
      {!collapsed && <span className="flex-1 text-left tracking-tight truncate">{item.label}</span>}
      {unread > 0 && (
        <span
          className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold ring-2 ring-white
            ${collapsed ? 'absolute top-1 right-2 min-w-[16px] h-4 px-1 text-[9px]' : ''}`}
          data-testid={`${testidPrefix}support-tab-unread`}
        >
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </TabsTrigger>
  );

  if (!collapsed) return trigger;
  return (
    <Tooltip delayDuration={120}>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8} className="bg-slate-900 text-white border-slate-800">
        <p className="text-xs font-semibold">{item.label}</p>
        <p className="text-[11px] text-slate-300">{item.description}</p>
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * Vertical admin navigation. Renders real shadcn TabsTriggers so the existing
 * <Tabs value={mainTab}> state model is untouched.
 */
export const AdminSideNav = ({
  supportUnread = 0,
  collapsed = false,
  onToggleCollapse,
  variant = 'desktop',
  onNavigate,
  adminName,
  onLogout,
}) => {
  const isMobile = variant === 'mobile';
  const isCollapsed = collapsed && !isMobile;

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full w-full" data-testid={`admin-side-nav-${variant}`}>
        {/* Identity */}
        <div className={`flex items-center gap-2.5 border-b border-slate-100 bg-slate-50/60 h-14 flex-shrink-0 ${isCollapsed ? 'justify-center px-0' : 'px-4'}`}>
          <div className="w-8 h-8 rounded-md bg-[#1B5E20] flex items-center justify-center flex-shrink-0">
            <ShieldAlert className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          {!isCollapsed && (
            <div className="min-w-0">
              <p className="text-sm font-bold tracking-tight text-slate-900 leading-none">Admin Console</p>
              <p className="text-[11px] text-slate-500 mt-0.5 truncate">JustDeliver operations</p>
            </div>
          )}
        </div>

        {/* Destinations */}
        <div className="flex-1 overflow-y-auto py-3">
          <TabsList className="flex flex-col h-auto w-full bg-transparent p-0 gap-1 justify-start border-none rounded-none shadow-none">
            {ADMIN_NAV_SECTIONS.map((section) => (
              <div key={section.kicker} className="w-full px-2 pb-2">
                {!isCollapsed && (
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-1.5 py-1.5">{section.kicker}</p>
                )}
                {isCollapsed && <div className="mx-auto w-8 border-t border-slate-200 my-2" />}
                <div className="flex flex-col gap-1">
                  {section.items.filter((item) => !item.requiresDirectory || DIRECTORY_ENABLED).map((item) => (
                    <NavItem
                      key={item.id}
                      item={item}
                      collapsed={isCollapsed}
                      supportUnread={supportUnread}
                      testidPrefix={isMobile ? 'mobile-' : ''}
                      onNavigate={onNavigate}
                    />
                  ))}
                </div>
              </div>
            ))}
          </TabsList>
        </div>

        {/* Footer */}
        <div className={`border-t border-slate-100 bg-slate-50/60 py-3 flex-shrink-0 ${isCollapsed ? 'px-2' : 'px-3'}`}>
          {!isCollapsed && adminName && (
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-800 truncate">{adminName}</p>
                <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Administrator</p>
              </div>
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="w-8 h-8 rounded-md border border-slate-200 bg-white hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-red-600 transition-colors"
                  title="Sign out"
                  data-testid="admin-nav-logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
          {!isMobile && (
            <button
              onClick={onToggleCollapse}
              className={`h-9 rounded-md border border-slate-200 bg-white hover:bg-slate-100 flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors
                ${isCollapsed ? 'w-full justify-center' : 'w-full justify-start px-3'}`}
              data-testid="admin-nav-collapse-toggle"
            >
              {isCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
              {!isCollapsed && <span className="text-xs font-medium">Collapse menu</span>}
            </button>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default AdminSideNav;
